import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

import {
  ChatSurface,
  ChatSurfaceMessage,
  ChatSurfaceWidget,
} from '@/components/chat/chat-surface';
import { WidgetRenderer } from '@/components/chat/widgets';
import { ThemedText } from '@/components/themed-text';
import {
  isAgentUnavailable,
  useAdjustInterview,
  useAdvanceInterview,
  useInterview,
  useInterviewDraft,
} from '@/hooks/use-onboarding-interview';
import {
  AdjustInput,
  InterviewTurn,
  InterviewTurnRecord,
  ONBOARDING_DRAFT_STATUS,
  ONBOARDING_SLOT,
} from '@/types/onboarding-interview';

interface Props {
  /** Rascunho pronto: a tela troca a conversa pelo resumo. */
  onFinished: () => void;
  /** Acabou sem fechar: oferecer a lista de ramos. */
  onExhausted: () => void;
  /** `503 AGENT_UNAVAILABLE`, inclusive no meio da conversa (T-24). */
  onAgentDown: () => void;
  brandColor?: string;
}

/**
 * A entrevista de onboarding: o prestador conversa e o sistema se configura.
 *
 * A tela **nunca abre em branco nem em formulário** — ao montar, pede o turno
 * de abertura (texto vazio, que não gasta turno) e o agente já entra
 * perguntando o que ele faz. Fechou o app no meio? O transcript vem do
 * rascunho e a conversa continua de onde parou (cenário 7).
 *
 * O que se escreve aqui é texto; o que se corrige por toque são os widgets,
 * que fazem `PATCH /draft` sem passar pelo modelo.
 */
export function InterviewChat({
  onFinished,
  onExhausted,
  onAgentDown,
  brandColor,
}: Props) {
  const draft = useInterviewDraft();
  const turn = useInterview();
  const advance = useAdvanceInterview();
  const adjust = useAdjustInterview();

  /**
   * As mutations em si trocam de identidade a cada render; `mutateAsync` não.
   * É por isso que só ela entra nas dependências: com o objeto inteiro, todo
   * callback do chat era refeito a cada render e a lista redesenhava os
   * cartões antigos junto.
   */
  const sendTurn = turn.mutateAsync;
  const sendAdvance = advance.mutateAsync;
  const sendAdjust = adjust.mutateAsync;

  const [thread, setThread] = useState<ChatSurfaceMessage[]>([]);
  const [failed, setFailed] = useState(false);
  /** Abrir a conversa é uma vez só, mesmo com o rascunho revalidando. */
  const opened = useRef(false);
  /**
   * Uma resposta por vez.
   *
   * Sem isto, sair clicando rápido em dois cartões mandava dois turnos ao
   * mesmo tempo: o segundo respondia um passo que o prestador nem tinha visto,
   * e a conversa terminava em estado que não bate com a tela.
   */
  const busy = useRef(false);
  /** A bolha provisória do toque, que a frase do servidor vai substituir. */
  const echoId = useRef<string | null>(null);

  const appendAgent = useCallback((reply: InterviewTurn) => {
    setThread(prev => {
      const lastAgent = [...prev].reverse().find(message => !message.mine);
      // O `open()` devolve a última pergunta do agente quando retoma: ela já
      // está no transcript e repetir viraria eco na tela.
      if (lastAgent?.body === reply.message) return prev;

      const next = [...prev];

      // A bolha dele já está na tela desde o toque; o servidor manda a versão
      // oficial da frase e ela entra no lugar da provisória.
      const pendingEcho = echoId.current;
      echoId.current = null;
      if (pendingEcho) {
        const at = next.findIndex(message => message.id === pendingEcho);
        if (at >= 0 && reply.echo) {
          next[at] = { ...next[at], body: reply.echo };
        }
      } else if (reply.echo) {
        next.push({
          id: `me-${next.length}-${Date.now()}`,
          body: reply.echo,
          mine: true,
        });
      }

      next.push({
        id: `agent-${next.length}-${Date.now()}`,
        body: reply.message,
        mine: false,
        widget: reply.widget,
      });
      return next;
    });
  }, []);

  /**
   * A fala dele entra **no toque**, não na resposta do servidor.
   *
   * Antes, tocar no cartão mostrava os pontinhos do agente primeiro e só
   * depois aparecia o que ele tinha respondido — a conversa saía fora de
   * ordem, como se o agente falasse antes da pergunta ser respondida.
   */
  const showEcho = useCallback((text?: string) => {
    if (!text) return;
    const id = `me-${Date.now()}`;
    echoId.current = id;
    setThread(prev => [...prev, { id, body: text, mine: true }]);
  }, []);

  const handleTurn = useCallback(
    (reply: InterviewTurn) => {
      appendAgent(reply);
      if (reply.exhausted) onExhausted();
      else if (reply.finished) onFinished();
    },
    [appendAgent, onExhausted, onFinished],
  );

  const handleError = useCallback(
    (error: unknown) => {
      if (isAgentUnavailable(error)) {
        onAgentDown();
        return;
      }
      setFailed(true);
    },
    [onAgentDown],
  );

  const send = useCallback(
    async (text: string) => {
      setFailed(false);
      setThread(prev => [
        ...prev,
        { id: `me-${prev.length}-${Date.now()}`, body: text, mine: true },
      ]);
      try {
        handleTurn(await sendTurn(text));
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, handleTurn, sendTurn],
  );

  /**
   * "Pronto" no cartão: o prestador respondeu por toque, não por texto.
   *
   * O `PATCH /draft` do widget já gravou a resposta; o que falta é a conversa
   * andar. Sem isto o agente ficava parado repetindo a mesma pergunta depois
   * de o prestador já ter corrigido tudo no cartão.
   */
  const handleAdvance = useCallback(
    async (echo?: string) => {
      // Toque duplo no cartão mandava duas vezes: a primeira já trocava o
      // passo, e a segunda respondia o passo seguinte sem ele ver a pergunta.
      if (busy.current) return;
      busy.current = true;
      setFailed(false);
      showEcho(echo);
      try {
        handleTurn(await sendAdvance());
      } catch (error) {
        handleError(error);
      } finally {
        busy.current = false;
      }
    },
    [handleError, handleTurn, sendAdvance, showEcho],
  );

  /**
   * O ajuste dirigido: qual parte ele quer mudar, depois do resumo.
   *
   * `done` fecha e volta para o resumo — é o `finished` do turno que a tela
   * escuta, então nada aqui decide navegação.
   */
  const handleAdjust = useCallback(
    async (input: AdjustInput, echo?: string) => {
      if (busy.current) return;
      busy.current = true;
      setFailed(false);
      showEcho(echo);
      try {
        handleTurn(await sendAdjust(input));
      } catch (error) {
        handleError(error);
      } finally {
        busy.current = false;
      }
    },
    [handleError, handleTurn, sendAdjust, showEcho],
  );

  // O cartão guardado é snapshot: quando o toque no widget muda o rascunho, o
  // backend redesenha o cartão da última fala e o chat precisa acompanhar —
  // senão o prestador corrige o preço e continua vendo o valor velho.
  useEffect(() => {
    const latest = draft.data?.messages.at(-1);
    if (!opened.current || !latest || latest.role !== 'agent') return;
    setThread(prev => {
      const at = prev.findLastIndex(message => !message.mine);
      if (at < 0 || prev[at].widget === latest.widget) return prev;
      const next = [...prev];
      next[at] = { ...next[at], widget: latest.widget ?? null };
      return next;
    });
  }, [draft.data]);

  // Abertura (ou retomada): o transcript remonta o chat e o turno vazio traz a
  // pergunta da vez sem custar um turno de entrevista.
  useEffect(() => {
    if (opened.current || !draft.data) return;
    opened.current = true;
    setThread(draft.data.messages.map(toBubble));
    const ready = draft.data.status === ONBOARDING_DRAFT_STATUS.READY;
    void (async () => {
      try {
        // Voltando do resumo para ajustar: o roteiro não tem o que perguntar,
        // então a conversa abre no menu de partes em vez de devolver o resumo.
        handleTurn(ready ? await sendAdjust({}) : await sendTurn(undefined));
      } catch (error) {
        handleError(error);
      }
    })();
  }, [draft.data, handleError, handleTurn, sendAdjust, sendTurn]);

  const sending = turn.isPending || advance.isPending || adjust.isPending;
  /**
   * O campo de texto some quando o passo é de cartão.
   *
   * Com o roteiro fixo, quase toda pergunta se responde tocando; deixar a
   * caixa de escrever aberta convida a digitar uma resposta que o passo não
   * espera — e a única pergunta realmente aberta é a primeira, a do ramo, que
   * vem sem cartão nenhum.
   *
   * Exceção: o "como receber". Lá o cartão escolhe, mas escrever é o jeito de
   * tirar dúvida sobre as opções — no roteiro e no ajuste depois do resumo.
   */
  const lastAgent = [...thread].reverse().find(message => !message.mine);
  const cardOnly =
    !!lastAgent?.widget &&
    (lastAgent.widget.data as { slot?: string } | undefined)?.slot !== ONBOARDING_SLOT.PAYMENTS;

  /**
   * Estável de propósito: quem decide se o cartão aceita toque é a superfície,
   * que já sabe qual é o último turno e se há resposta em voo. Enquanto isto
   * dependia de `lastId`/`pending` daqui, cada envio trocava a função e a
   * lista redesenhava todos os cartões antigos — o trabalho que segurava a
   * própria fala do prestador para aparecer.
   */
  const renderWidget = useCallback(
    (
      widget: ChatSurfaceWidget,
      _message: ChatSurfaceMessage,
      editable: boolean,
    ) => (
      <WidgetRenderer
        widget={widget}
        editable={editable}
        onAdvance={handleAdvance}
        onAdjust={handleAdjust}
      />
    ),
    [handleAdvance, handleAdjust],
  );

  return (
    <ChatSurface
      messages={thread}
      onSend={text => void send(text)}
      sending={sending}
      isTyping={sending}
      isLoading={draft.isLoading}
      disabled={cardOnly}
      brandColor={brandColor}
      placeholder="Responda aqui"
      renderWidget={renderWidget}
      // A faixa fixa embaixo saiu: um bloco branco permanente entre a lista e
      // o campo cortava a última bolha, e o atalho que morava nele competia
      // com a conversa. Só o aviso de falha aparece — e só quando falha.
      footer={
        failed ? (
          <View className="px-4 pb-2">
            <ThemedText className="text-center text-xs text-error-600">
              Não consegui responder agora. Escreva de novo, por favor.
            </ThemedText>
          </View>
        ) : undefined
      }
    />
  );
}

/** Uma fala guardada no rascunho vira bolha; o cartão vem junto. */
function toBubble(
  record: InterviewTurnRecord,
  index: number,
): ChatSurfaceMessage {
  return {
    id: `${record.role}-${index}-${record.at}`,
    body: record.text,
    mine: record.role === 'provider',
    widget: record.widget ?? null,
  };
}
